import * as Sentry from '@sentry/browser';
import { BusinessInfo } from 'api/account/business/dtos';
import { useBusinessInfo } from 'api/account/business/hooks';
import { BasicBusinessInfoProvider } from 'providers/basic-business-info-provider/BasicBusinessInfoProvider';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { isDev, logger } from 'utils';

export type DefaultFormKeys =
  | 'email'
  | 'number'
  | 'dateOfLeaving'
  | 'dateOfArrival'
  | 'gender'
  | 'dateOfBirth';

export interface IBusinessContext extends Omit<BusinessInfo, 'studentFormBaseFields' | 'id'> {
  businessId: number;
  studentFormBaseFields?: StudentFormBaseFields;
  hasLoaded: boolean;
}

export type StudentFormBaseFields = Partial<Record<DefaultFormKeys, FormBaseFieldsRules>>;

export type FormBaseFieldsRules = { required: boolean; visible: boolean };

const defaultBusinessContextState: IBusinessContext = {
  hasLoaded: false,
  name: '',
  hours: [
    ['08:00', '18:00'], // monday
    ['08:00', '18:00'], // tuesday
    ['08:00', '18:00'], // wednesday
    ['08:00', '18:00'], // thursday
    ['08:00', '18:00'], // friday
    ['08:00', '18:00'], // saturday
    ['08:00', '18:00'], // sunday
  ],
  slug: '',
  isConfigured: true,
  createdAt: '',
  activeSeason: {
    id: 0,
    name: '',
    isActive: true,
    startAt: '',
    endAt: '',
  },
  settings: {
    id: 0,
    timezone: '',
    defaults: {
      lessonDuration: 60,
      lessonIncrement: 15,
    },
    notifications: {
      lessonDateChanged: false,
    },
  },
  businessId: 0,
};

const BusinessContext = React.createContext<IBusinessContext>(defaultBusinessContextState);

const BusinessProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const params = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const businessId = Number(params.businessId || 0);

  const isEnabled = businessId > 0;

  const businessInfoQuery = useBusinessInfo(businessId, {
    enabled: isEnabled,
    meta: {
      error: {
        notification: {
          message: t('errors.generic.title'),
          description: t('errors.business_fetch.description'),
        },
      },
    },
  });

  if (!isEnabled || businessInfoQuery.isError) {
    console.error('Wrong business id', businessInfoQuery.failureReason);
    logger.log(businessInfoQuery.error);
    navigate('/');
  }

  if (!isDev()) {
    const scope = Sentry.getCurrentScope();
    scope.setTag('business.id', businessId);
    scope.setTag('business.name', businessInfoQuery.data?.name);
  }

  return (
    <BusinessContext
      value={{
        ...(businessInfoQuery.data || defaultBusinessContextState),
        businessId,
      }}
    >
      <BasicBusinessInfoProvider
        state={{
          hasLoaded: false,
          ...businessInfoQuery.data,
          id: businessId,
        }}
      >
        {children}
      </BasicBusinessInfoProvider>
    </BusinessContext>
  );
};

export default BusinessProvider;
export { BusinessContext };
